type DateLike = string | number | Date | null | undefined;
export const LOCAL_DATETIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";

export abstract class DateUtils {
  static readonly MY_DATE_FORMATS = {
    parse: {
      dateInput: 'DD/MM/yyyy',
    },
    display: {
      dateInput: 'DD/MM/yyyy',
      monthYearLabel: 'MMM yyyy',
      dateA11yLabel: 'DD/MM/yyyy',
      monthYearA11yLabel: 'MMMM yyyy',
    },
  };

  static readonly ISO_DATE_FORMAT = 'yyyy-MM-dd';

  static readonly MONTH_KEYS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
  ];

  static isSameDateTime(date1: DateLike, date2: DateLike): boolean {
    return this.compareDateTime(date1, date2) === 0;
  }

  /**
   * Compare two dates, without timezone
   *
   * @param date1
   * @param date2
   *
   * @return -1 if date1 is before date2, 1 if date1 after date2 and 0 if date1 equal date2
   */
  static compareDays(date1: Date | string, date2: Date | string): number {
    const date1Date = new Date(date1);
    const date2Date = new Date(date2);

    const date1UTC = new Date(date1Date.getFullYear(), date1Date.getMonth(), date1Date.getDate());
    const date2UTC = new Date(date2Date.getFullYear(), date2Date.getMonth(), date2Date.getDate());

    if (date1UTC.getTime() < date2UTC.getTime()) {
      return -1;
    }

    if (date1UTC.getTime() > date2UTC.getTime()) {
      return 1;
    }

    return 0;
  }

  /**
   * Test if a date is today.
   *
   * @param date
   *
   * @return true if the date is today, false otherwise.
   */
  static isToday(date: Date | string): boolean {
    const dateToTest = new Date(date);
    const today = new Date();
    return (
      today.getDate() === dateToTest.getDate() &&
      today.getMonth() === dateToTest.getMonth() &&
      today.getFullYear() === dateToTest.getFullYear()
    );
  }

  /**
   * Transforms a Date to a yyyy-MM-DD string
   * @param date
   */
  static formatDateYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  /**
   * Returns a month shortened string representation given a month index (starting from 0)
   * @param monthIndex
   * @returns a short string representation of the month
   */
  static getMonthAsShortString(monthIndex: number): string {
    const months: string[] = [
      'JAN',
      'FÉV',
      'MAR',
      'AVR',
      'MAI',
      'JUIN',
      'JUIL',
      'AOÛT',
      'SEP',
      'OCT',
      'NOV',
      'DÉC',
    ];
    return months[monthIndex];
  }

  static isBetween(date1: string | number | Date, dateMin: DateLike, dateMax: DateLike): boolean {
    if (!dateMin && !dateMax) {
      return true;
    }

    if (dateMax) {
      return this.compareDateTime(date1, dateMax) <= 0;
    } else {
      return this.compareDateTime(date1, dateMin) >= 0;
    }
  }

  static compareDateTime(date1: DateLike, date2: DateLike): number {
    const date1timestamp: number = this.toTimestamp(date1);
    const date2timestamp: number = this.toTimestamp(date2);

    if (date1timestamp > date2timestamp) {
      return 1;
    } else if (date1timestamp < date2timestamp) {
      return -1;
    }
    return 0;
  }

  private static toTimestamp(date: DateLike): number {
    if (!date) {
      return 0;
    }

    if (typeof date === 'string') {
      return Date.parse(date);
    } else if (date instanceof Date) {
      return date.getTime();
    }
    return date;
  }

  static combineDateAndTime(date: Date, time: Date | string): Date {
    if (typeof time === 'string') {
      time = this.parseTimepicker(time)!;
    }

    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();

    const combined = new Date(date);
    combined.setHours(hours, minutes, seconds, 0);

    return combined;
  }

  static parseTimepicker(value: string): Date | null {
    if (!value) return null;

    const cleaned = value.replaceAll(/\D/g, '');
    if (cleaned.length < 3) return null;

    const hours = Number(cleaned.slice(0, 2));
    const minutes = Number(cleaned.slice(2, 4) || '0');

    if (hours > 23 || minutes > 59) return null;

    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  static dateToTimepicker(value: string): string {
    const date = new Date(value);

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}${minutes}`;
  }

  static timeDiffInMinutes(date1: Date, date2: Date): number {
    const hoursDiff = date2.getHours() - date1.getHours();
    const minutesDiff = date2.getMinutes() - date1.getMinutes();
    return hoursDiff * 60 + minutesDiff;
  }

  static addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  static substractDays(date: Date, days: number): Date {
    return DateUtils.addDays(date, -days);
  }
}
